<template>
  <section class="admin-content" id="add-record">
    <Navbar />
    <main class="admin-main">

      <!-- Page Header -->
      <div class="page-header" style="background: linear-gradient(135deg, #17a2b8 0%, #0c7a8a 100%); padding: 32px 0;">
        <div class="container">
          <div class="d-flex align-items-center text-white">
            <div class="mr-3"
              style="width: 48px; height: 48px; background: rgba(255,255,255,0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center;">
              <i class="fe fe-file-plus" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-0 font-weight-bold">New Health Encounter</h4>
              <small style="opacity: 0.8;">Record a patient visit and clinical findings</small>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <form @submit.prevent="AddRecord">
            <!-- Agency staff: the encounter is recorded under the facility chosen here -->
            <div v-if="agencyStaff" class="form-group mb-4">
              <label class="font-weight-bold small mb-1">Facility where the patient was seen <span class="text-danger">*</span></label>
              <v-select v-model="encounter_facility" :reduce="agency_name => agency_name.id" label="agency_name"
                :options="providers" :loading="providersLoading" placeholder="Choose the facility" />
            </div>

            <!-- Telemedicine consultation this encounter belongs to -->
            <div v-if="consultation" class="alert mb-4" style="background:#f5f3ff;border:1px solid #ddd6fe;color:#4c1d95;">
              <div class="d-flex align-items-center">
                <i class="fe fe-video mr-3" style="font-size:1.4rem;"></i>
                <div>
                  <strong>Telemedicine consultation</strong> for {{ consultation.patient_name }}
                  ({{ consultation.patient_id_card }}) on {{ consultation.appointDate }}
                  <div class="small">Reason: {{ consultation.reason }}</div>
                  <div class="small" v-if="consultation.referring_provider">
                    Sent by {{ consultation.referring_provider.agency_name }}
                  </div>
                </div>
              </div>
            </div>

            <!-- ── 1. VISIT DETAILS ─────────────────────────────── -->
            <div class="card shadow-sm mb-4">
              <div class="card-header border-0 pt-4 pb-2 px-4" style="background: #f8f9fa;">
                <div class="d-flex align-items-center">
                  <span class="badge badge-info mr-2"
                    style="border-radius: 50%; width: 26px; height: 26px; line-height: 26px; font-size: 13px;">1</span>
                  <h6 class="mb-0 font-weight-bold text-dark">Visit Details</h6>
                </div>
              </div>
              <div class="card-body px-4 pt-3">
                <div class="form-row">
                  <div class="form-group col-md-4">
                    <label class="small font-weight-bold text-secondary">Date of Visit <span
                        class="text-danger">*</span></label>
                    <input required class="form-control" type="date" v-model="record.date_of_visit" />
                  </div>
                  <div class="form-group col-md-4">
                    <label class="small font-weight-bold text-secondary">Treatment Type <span
                        class="text-danger">*</span></label>
                    <select required class="form-control" v-model="record.treatment_type">
                      <option value="Primary">Primary Treatment</option>
                      <option value="Secondary">Secondary Treatment</option>
                    </select>
                  </div>
                  <div class="form-group col-md-4">
                    <label class="small font-weight-bold text-secondary">Reason for Visit <span
                        class="text-danger">*</span></label>
                    <select class="form-control" v-model="record.reasonVisit" required>
                      <option value="General Consultation">General Consultation</option>
                      <option value="Telemedicine Consultation">Telemedicine Consultation</option>
                      <option value="Laboratory Test">Laboratory Test</option>
                      <option value="Regular Check ups">Regular Check ups</option>
                      <option value="Emergency">Emergency</option>
                      <option value="Referred In">Referred In</option>
                      <option value="Review/follow-up">Review / Follow-up</option>
                      <option value="Specialist Consultation">Specialist Consultation</option>
                      <option value="Out of State">Out of State</option>
                      <option value="ANC">ANC</option>
                      <option value="PNC">PNC</option>
                      <option value="Labour Delivery">Labour Delivery</option>
                      <option value="Immunization">Immunization</option>
                      <option value="Caeserian Section">Caesarean Section</option>
                      <option value="Medical Outreach">Medical Outreach</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── 2. PATIENT SEARCH ────────────────────────────── -->
            <div class="card shadow-sm mb-4">
              <div class="card-header border-0 pt-4 pb-2 px-4" style="background: #f8f9fa;">
                <div class="d-flex align-items-center">
                  <span class="badge badge-info mr-2"
                    style="border-radius: 50%; width: 26px; height: 26px; line-height: 26px; font-size: 13px;">2</span>
                  <h6 class="mb-0 font-weight-bold text-dark">Patient Lookup</h6>
                </div>
              </div>
              <div class="card-body px-4 pt-3">
                <div class="form-row">
                  <div class="form-group col-md-8">
                    <label class="small font-weight-bold text-secondary">Search Enrollee</label>
                    <div class="input-group">
                      <div class="input-group-prepend">
                        <span class="input-group-text bg-white"><i class="fe fe-search text-muted"></i></span>
                      </div>
                      <input required type="text" class="form-control border-left-0"
                        placeholder="Enrollee ID, First Name, Last Name…" v-model="searchkey"
                        @change="searchIDCard" @keyup.enter="searchIDCard" />
                      <div class="input-group-append">
                        <button type="button" class="btn btn-info btn-sm px-3" @click="searchIDCard"
                          :disabled="isLoading" title="Search enrollee">
                          <i class="fe fe-search mr-1"></i> Search
                        </button>
                      </div>
                    </div>
                    <div class="mt-1">
                      <label class="small text-muted">
                        <input class="mr-1" type="checkbox" v-model="record.is_corridor" @change="searchIDCard" />
                        Out-of-state / Corridor enrollee
                      </label>
                    </div>
                  </div>
                  <div class="form-group col-md-4">
                    <label class="small font-weight-bold text-secondary">Select Enrollee <span
                        class="text-danger">*</span></label>
                    <select class="form-control" v-model="selected_enrollee" required>
                      <option :value="user" v-for="user in search_result" :key="user.id">
                        {{ user.user_query }}
                      </option>
                    </select>
                    <small class="text-muted">{{ Array.isArray(search_result) ? search_result.length : 0 }} result(s)
                      found</small>
                  </div>
                </div>

                <!-- Patient summary card -->
                <div v-if="userDetails && selected_enrollee && selected_enrollee.id" class="p-3 rounded mt-1"
                  style="background: #e8f7f9; border: 1px solid #b2e0e8;">
                  <div class="d-flex align-items-center">
                    <img v-if="selected_enrollee.user_image" :src="`${selected_enrollee.image_url}`"
                      class="rounded-circle mr-3"
                      style="width: 60px; height: 60px; object-fit: cover; border: 2px solid #17a2b8;" alt="Patient"
                      onerror="this.onerror=null; this.src='/assets/img/claimify_logo.png'" />
                    <div class="flex-grow-1">
                      <h6 class="mb-0 font-weight-bold">{{ selected_enrollee.full_name }}</h6>
                      <div class="d-flex flex-wrap mt-1" style="gap: 16px;">
                        <small class="text-muted"><i class="fe fe-calendar mr-1"></i>DOB: {{ selected_enrollee.dob
                          }}</small>
                        <small class="text-muted"><i class="fe fe-tag mr-1"></i>{{ selected_enrollee.sector }}</small>
                        <small class="text-muted">
                          <i class="fe fe-phone mr-1"></i>
                          <input v-phone type="text" class="border-0 bg-transparent p-0 text-muted small"
                            style="outline: none; min-width: 120px;" ref="enrolleePhone" v-model="selected_enrollee.phone_number" />
                        </small>
                      </div>
                    </div>
                    <div class="d-flex align-items-center" style="gap: 8px;">
                      <span class="badge badge-success">Active</span>
                      <button v-if="selected_enrollee.user_image" type="button" class="btn btn-sm btn-outline-info"
                        @click="startFaceVerify" :disabled="faceVerify.loading">
                        <i class="fe fe-camera mr-1"></i>
                        {{ faceVerify.loading ? 'Loading…' : 'Verify Face' }}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Face Verification Panel -->
                <div v-if="faceVerify.active" class="mt-3 p-3 rounded"
                  style="background: #f4f6fb; border: 1px solid #d0d9ee;">
                  <div class="d-flex align-items-center mb-3">
                    <i class="fe fe-shield text-info mr-2"></i>
                    <strong style="font-size: 0.9rem;">Identity Verification</strong>
                    <button type="button" class="btn btn-sm btn-link ml-auto text-muted p-0" @click="resetFaceVerify">
                      <i class="fe fe-x"></i>
                    </button>
                  </div>

                  <!-- Loading models -->
                  <div v-if="faceVerify.loading" class="text-center py-3">
                    <div class="spinner-border spinner-border-sm text-info mr-2"></div>
                    <span class="text-muted small">Loading face recognition models…</span>
                  </div>

                  <!-- Ready to capture -->
                  <div v-else>
                    <div class="row">
                      <!-- Stored photo -->
                      <div class="col-6 text-center">
                        <p class="small text-muted mb-2 font-weight-bold text-uppercase" style="letter-spacing: .04em;">
                          Enrolled Photo</p>
                        <img :src="`${selected_enrollee.image_url}`" class="rounded"
                          style="width: 100%; max-width: 180px; height: 180px; object-fit: cover; border: 3px solid #17a2b8;"
                          onerror="this.onerror=null; this.src='/assets/img/claimify_logo.png'" />
                      </div>

                      <!-- Live capture -->
                      <div class="col-6 text-center">
                        <p class="small text-muted mb-2 font-weight-bold text-uppercase" style="letter-spacing: .04em;">
                          Live Capture</p>
                        <div v-if="!faceVerify.capturedImage"
                          class="rounded d-flex flex-column align-items-center justify-content-center"
                          style="width: 100%; max-width: 180px; height: 180px; border: 3px dashed #b2bec3; background: #fff; margin: 0 auto; cursor: pointer;"
                          @click="captureForVerify">
                          <i class="fe fe-camera text-muted" style="font-size: 2rem;"></i>
                          <small class="text-muted mt-2">Tap to capture</small>
                        </div>
                        <img v-else :src="faceVerify.capturedImage" class="rounded"
                          style="width: 100%; max-width: 180px; height: 180px; object-fit: cover; border: 3px solid #6c757d;" />
                      </div>
                    </div>

                    <!-- Result badge -->
                    <div v-if="faceVerify.result !== null" class="mt-3 text-center">
                      <div v-if="faceVerify.result === 'match'"
                        class="alert alert-success d-flex align-items-center justify-content-center mb-2"
                        style="border-radius: 8px;">
                        <i class="fe fe-check-circle mr-2" style="font-size: 1.2rem;"></i>
                        <span><strong>Identity Verified</strong> — {{ faceVerify.confidence }}% confidence</span>
                      </div>
                      <div v-else-if="faceVerify.result === 'no-match'"
                        class="alert alert-danger d-flex align-items-center justify-content-center mb-2"
                        style="border-radius: 8px;">
                        <i class="fe fe-alert-circle mr-2" style="font-size: 1.2rem;"></i>
                        <span><strong>Face Does Not Match</strong> — {{ faceVerify.confidence }}% similarity</span>
                      </div>
                      <div v-else-if="faceVerify.result === 'no-face'"
                        class="alert alert-warning d-flex align-items-center justify-content-center mb-2"
                        style="border-radius: 8px;">
                        <i class="fe fe-alert-triangle mr-2"></i>
                        <span>No face detected in one or both images. Try again in better lighting.</span>
                      </div>
                    </div>

                    <!-- Actions -->
                    <div class="d-flex justify-content-center mt-3" style="gap: 8px;">
                      <button type="button" class="btn btn-info btn-sm" @click="captureForVerify"
                        :disabled="faceVerify.comparing">
                        <i class="fe fe-camera mr-1"></i>
                        <span v-if="faceVerify.comparing">
                          <span class="spinner-border spinner-border-sm mr-1"></span> Comparing…
                        </span>
                        <span v-else>{{ faceVerify.capturedImage ? 'Retake Photo' : 'Capture Photo' }}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── 3. VITALS ────────────────────────────────────── -->
            <div class="card shadow-sm mb-4">
              <div class="card-header border-0 pt-3 pb-3 px-4" style="background: #f8f9fa;">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <span class="badge badge-info mr-2"
                      style="border-radius: 50%; width: 26px; height: 26px; line-height: 26px; font-size: 13px;">3</span>
                    <h6 class="mb-0 font-weight-bold text-dark">Vital Signs</h6>
                  </div>
                  <button type="button" class="btn btn-sm"
                    :class="showVitals ? 'btn-outline-danger' : 'btn-outline-info'" @click="showVitals = !showVitals">
                    <i class="fe mr-1" :class="showVitals ? 'fe-minus' : 'fe-plus'"></i>
                    {{ showVitals ? 'Hide Vitals' : 'Add Vitals' }}
                  </button>
                </div>
              </div>

              <div v-show="showVitals" class="card-body px-4 pt-3">
                <!-- BP badge indicator -->
                <div class="row mb-3">
                  <div class="col-12">
                    <small class="text-muted">Fill in the measurements taken at this visit. BMI is calculated
                      automatically.</small>
                  </div>
                </div>

                <div class="form-row">
                  <!-- Temperature -->
                  <div class="form-group col-md-3 col-6">
                    <label class="small font-weight-bold text-secondary">Temperature (°C)</label>
                    <div class="input-group input-group-sm">
                      <input type="number" step="0.1" min="30" max="45" class="form-control"
                        v-model="record.temperature" placeholder="36.5" />
                      <div class="input-group-append"><span class="input-group-text">°C</span></div>
                    </div>
                  </div>

                  <!-- Pulse Rate -->
                  <div class="form-group col-md-3 col-6">
                    <label class="small font-weight-bold text-secondary">Pulse Rate (bpm)</label>
                    <div class="input-group input-group-sm">
                      <input type="number" min="0" max="300" class="form-control" v-model="record.pulse_rate"
                        placeholder="72" />
                      <div class="input-group-append"><span class="input-group-text">bpm</span></div>
                    </div>
                  </div>

                  <!-- Respiratory Rate -->
                  <div class="form-group col-md-3 col-6">
                    <label class="small font-weight-bold text-secondary">Respiratory Rate</label>
                    <div class="input-group input-group-sm">
                      <input type="number" min="0" max="100" class="form-control" v-model="record.respiratory_rate"
                        placeholder="18" />
                      <div class="input-group-append"><span class="input-group-text">/min</span></div>
                    </div>
                  </div>

                  <!-- Blood Glucose -->
                  <div class="form-group col-md-3 col-6">
                    <label class="small font-weight-bold text-secondary">Blood Glucose</label>
                    <div class="input-group input-group-sm">
                      <input type="number" step="0.1" min="0" class="form-control" v-model="record.blood_glucose"
                        placeholder="5.5" />
                      <div class="input-group-append"><span class="input-group-text">mmol/L</span></div>
                    </div>
                  </div>
                </div>

                <!-- Blood Pressure row with visual separator -->
                <div class="p-3 rounded mb-3" style="background: #fff8e1; border: 1px solid #ffe082;">
                  <p class="small font-weight-bold text-warning mb-2"><i class="fe fe-activity mr-1"></i>Blood Pressure
                  </p>
                  <div class="form-row">
                    <div class="form-group col-md-6 mb-0">
                      <label class="small font-weight-bold text-secondary">Systolic (mmHg)</label>
                      <div class="input-group input-group-sm">
                        <input type="number" min="0" max="300" class="form-control" v-model="record.systolic"
                          placeholder="120" />
                        <div class="input-group-append"><span class="input-group-text">mmHg</span></div>
                      </div>
                    </div>
                    <div class="form-group col-md-6 mb-0">
                      <label class="small font-weight-bold text-secondary">Diastolic (mmHg)</label>
                      <div class="input-group input-group-sm">
                        <input type="number" min="0" max="200" class="form-control" v-model="record.diastolic"
                          placeholder="80" />
                        <div class="input-group-append"><span class="input-group-text">mmHg</span></div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Anthropometrics row with BMI -->
                <div class="p-3 rounded" style="background: #e8f5e9; border: 1px solid #a5d6a7;">
                  <p class="small font-weight-bold text-success mb-2"><i class="fe fe-user mr-1"></i>Anthropometrics</p>
                  <div class="form-row align-items-end">
                    <div class="form-group col-md-4 mb-0">
                      <label class="small font-weight-bold text-secondary">Weight (kg)</label>
                      <div class="input-group input-group-sm">
                        <input type="number" step="0.1" min="0" max="500" class="form-control" v-model="record.weight"
                          placeholder="70" @input="calculateBMI" />
                        <div class="input-group-append"><span class="input-group-text">kg</span></div>
                      </div>
                    </div>
                    <div class="form-group col-md-4 mb-0">
                      <label class="small font-weight-bold text-secondary">Height (cm)</label>
                      <div class="input-group input-group-sm">
                        <input type="number" step="0.1" min="0" max="300" class="form-control" v-model="record.height"
                          placeholder="170" @input="calculateBMI" />
                        <div class="input-group-append"><span class="input-group-text">cm</span></div>
                      </div>
                    </div>
                    <div class="form-group col-md-4 mb-0">
                      <label class="small font-weight-bold text-secondary">BMI <span
                          class="text-muted">(auto)</span></label>
                      <div class="input-group input-group-sm">
                        <input type="text" class="form-control font-weight-bold" :class="bmiClass"
                          :value="record.bmi || '—'" readonly style="background: #fff;" />
                        <div class="input-group-append">
                          <span class="input-group-text" :class="bmiClass">{{ bmiLabel }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── 4. NOTES ─────────────────────────────────────── -->
            <div class="card shadow-sm mb-4">
              <div class="card-header border-0 pt-4 pb-2 px-4" style="background: #f8f9fa;">
                <div class="d-flex align-items-center">
                  <span class="badge badge-info mr-2"
                    style="border-radius: 50%; width: 26px; height: 26px; line-height: 26px; font-size: 13px;">4</span>
                  <h6 class="mb-0 font-weight-bold text-dark">Clinical Notes</h6>
                </div>
              </div>
              <div class="card-body px-4 pt-3">
                <div class="form-row">
                  <div class="form-group col-md-12">
                    <label class="small font-weight-bold text-secondary">Consultation Notes (Complaints)</label>
                    <textarea required class="form-control" rows="4" v-model="record.medications"
                      placeholder="Clinical notes, prescribed treatments…"></textarea>
                  </div>
                  <div class="form-group col-md-4">
                    <label class="small font-weight-bold text-secondary">Family History, Allergies &amp; Others</label>
                    <textarea class="form-control" rows="3" v-model="record.family_history"
                      placeholder="Family history, known allergies…"></textarea>
                  </div>
                  <div class="form-group col-md-4">
                    <label class="small font-weight-bold text-secondary">History Taking</label>
                    <textarea class="form-control" rows="3" v-model="record.history_taking"
                      placeholder="History of the presenting complaint…"></textarea>
                  </div>
                  <div class="form-group col-md-4">
                    <label class="small font-weight-bold text-secondary">Physical Examination</label>
                    <textarea class="form-control" rows="3" v-model="record.examination"
                      placeholder="Examination findings…"></textarea>
                  </div>
                  <div class="form-group col-md-4">
                    <label class="small font-weight-bold text-secondary">Next Visit Date</label>
                    <input type="date" class="form-control" v-model="record.next_visit" />
                  </div>
                </div>
              </div>
            </div>


            <!-- ── INVESTIGATIONS (services/investigations rows: primary care only) ── -->
            <div class="card shadow-sm mb-4">
              <div class="card-header border-0 pt-4 pb-2 px-4" style="background: #f8f9fa;">
                <div class="d-flex align-items-center">
                  <span class="badge badge-info mr-2"
                    style="border-radius: 50%; width: 26px; height: 26px; line-height: 26px; font-size: 13px;">{{ sectionNo.investigations }}</span>
                  <h6 class="mb-0 font-weight-bold text-dark">Investigations</h6>
                </div>
              </div>
              <div class="card-body px-4 pt-3">
                <p v-if="record.treatment_type === 'Primary' && catalogProgramme" class="small text-muted mb-3">
                  <i class="fe fe-filter mr-1"></i> Showing what applies to <b>{{ catalogProgramme }}</b>
                  ({{ serviceOptions.length }} investigations and services).
                </p>
                <template v-if="record.treatment_type === 'Primary'">
                <div v-for="entry in serviceRows('Service')" :key="'Service-' + entry.index"
                  class="p-3 mb-3 rounded" style="background: #f8fafc; border: 1px solid #e9ecef;">
                  <div class="form-row align-items-end">
                    <div class="form-group col-md-6 mb-2">
                      <label class="small font-weight-bold text-secondary">Select Investigation / Service</label>
                      <div :class="serviceErrors.includes(entry.index) ? 'is-invalid-select' : ''">
                        <v-select v-model="entry.svc.service" label="description" :options="serviceOptions"
                          @input="removeServiceError(entry.index)"
                          placeholder="Search investigation or service…" />
                      </div>
                      <small v-if="serviceErrors.includes(entry.index)" class="text-danger">Please select a investigation or service</small>
                    </div>
                    <div class="form-group col-md-5 mb-2">
                      <label class="small font-weight-bold text-secondary d-block">Input Mode</label>
                      <div class="btn-group btn-group-sm" role="group">
                        <button type="button" class="btn"
                          :class="entry.svc.doseMode === 'simple' ? 'btn-info' : 'btn-outline-secondary'"
                          @click="entry.svc.doseMode = 'simple'; entry.svc.dose = 1; entry.svc.frequency = 1">
                          <i class="fe fe-box mr-1"></i> Quantity
                        </button>
                        <button type="button" class="btn"
                          :class="entry.svc.doseMode === 'advanced' ? 'btn-info' : 'btn-outline-secondary'"
                          @click="entry.svc.doseMode = 'advanced'">
                          <i class="fe fe-sliders mr-1"></i> Freq / Days
                        </button>
                      </div>
                    </div>
                    <div class="form-group col-md-1 mb-2 text-right">
                      <label class="small font-weight-bold text-secondary d-block">&nbsp;</label>
                      <button type="button" class="btn btn-sm btn-outline-danger" @click="removeArray(entry.index)" title="Remove">
                        <i class="fe fe-trash-2"></i>
                      </button>
                    </div>
                  </div>
                  <div class="form-row" v-if="entry.svc.doseMode === 'simple'">
                    <div class="form-group col-md-3 mb-0">
                      <label class="small font-weight-bold text-secondary">Quantity</label>
                      <input type="number" min="1" class="form-control form-control-sm"
                        v-model.number="entry.svc.days" placeholder="1" />
                    </div>
                  </div>
                  <div class="form-row" v-if="entry.svc.doseMode === 'advanced'">
                    <div class="form-group col-md-3 mb-0">
                      <label class="small font-weight-bold text-secondary">Frequency</label>
                      <input type="number" min="1" class="form-control form-control-sm"
                        v-model.number="entry.svc.frequency" placeholder="1" />
                      <small class="text-muted">Times per day</small>
                    </div>
                    <div class="form-group col-md-3 mb-0">
                      <label class="small font-weight-bold text-secondary">Duration (Days)</label>
                      <input type="number" min="1" max="365" class="form-control form-control-sm"
                        v-model.number="entry.svc.days" placeholder="1" />
                    </div>
                  </div>
                </div>
                <button type="button" class="btn btn-sm btn-outline-info" @click="addArray('Service')">
                  <i class="fe fe-plus mr-1"></i> Add Investigation / Service
                </button>
                </template>
                <div class="form-group mt-3 mb-0">
                  <label class="small font-weight-bold text-secondary">Lab / Investigation Results</label>
                  <textarea class="form-control" rows="3" v-model="record.testResult"
                    placeholder="Results of the investigations done at this visit…"></textarea>
                </div>
              </div>
            </div>

            <!-- ── DIAGNOSIS (clinical assessment) ── -->
            <div class="card shadow-sm mb-4">
              <div class="card-header border-0 pt-4 pb-2 px-4" style="background: #f8f9fa;">
                <div class="d-flex align-items-center">
                  <span class="badge badge-info mr-2"
                    style="border-radius: 50%; width: 26px; height: 26px; line-height: 26px; font-size: 13px;">{{ sectionNo.diagnosis }}</span>
                  <h6 class="mb-0 font-weight-bold text-dark">Diagnosis &amp; Outcome</h6>
                </div>
              </div>
              <div class="card-body px-4 pt-3">
                <div class="form-row">
                  <div class="form-group col-md-4">
                    <label class="small font-weight-bold text-secondary">Main Diagnosis <span class="text-danger">*</span></label>
                    <div :class="diagnosisError ? 'is-invalid-select' : ''">
                      <v-select v-model="record.diagnosis" label="name" :options="diseases"
                        :filterable="false" @search="onDiagnosisSearch" @input="diagnosisError = false"
                        placeholder="ICD 11 Diagnosis…">
                        <template #option="option">
                          <span class="text-muted mr-1">({{ option.diagnosis_code }})</span>{{ option.name }}
                        </template>
                        <template #selected-option="option">
                          ({{ option.diagnosis_code }}) - {{ option.name }}
                        </template>
                        <template #no-options="{ search }">
                          <span class="text-muted small">{{ search.length >= 4 ? 'No results found' : 'Search ICD 11 Diagnoses…' }}</span>
                        </template>
                      </v-select>
                    </div>
                    <small v-if="diagnosisError" class="text-danger">Please select a diagnosis</small>
                  </div>
                  <div class="form-group col-md-4" v-if="record.diagnosis && record.diagnosis.name == 'Others'">
                    <label class="small font-weight-bold text-secondary">Specify Diagnosis <span
                        class="text-danger">*</span></label>
                    <input required type="text" class="form-control" v-model="record.other_diagnosis"
                      placeholder="Type diagnosis…" />
                  </div>
                  <div class="form-group col-md-4">
                    <label class="small font-weight-bold text-secondary">Other Diagnoses</label>
                    <v-select v-model="record.diagnoses" multiple label="name"
                      :options="diseases" :filterable="false" @search="onDiagnosisSearch"
                      placeholder="ICD 11 Diagnosis…">
                      <template #option="option">
                        <span class="text-muted mr-1">({{ option.diagnosis_code }})</span>{{ option.name }}
                      </template>
                      <template #selected-option="option">
                        ({{ option.diagnosis_code }}) - {{ option.name }}
                      </template>
                      <template #no-options="{ search }">
                        <span class="text-muted small">{{ search.length >= 4 ? 'No results found' : 'Search ICD 11 Diagnoses…' }}</span>
                      </template>
                    </v-select>
                  </div>
                  <div class="form-group col-md-4">
                    <label class="small font-weight-bold text-secondary">Encounter Outcome <span
                        class="text-danger">*</span></label>
                    <select required class="form-control" v-model="record.encounter_outcome">
                      <option value="">Select outcome…</option>
                      <option value="Admission">Admission</option>
                      <option value="Outpatient">Outpatient</option>
                      <option value="Referred Out">Referred Out</option>
                    </select>
                  </div>

                  <template v-if="record.encounter_outcome == 'Admission'">
                    <div class="form-group col-md-4">
                      <label class="small font-weight-bold text-secondary">Date of Admission <span
                          class="text-danger">*</span></label>
                      <input required class="form-control" type="date" v-model="record.date_of_admission" />
                    </div>
                    <div class="form-group col-md-4">
                      <label class="small font-weight-bold text-secondary">Date of Discharge <span
                          class="text-danger">*</span></label>
                      <input required class="form-control" type="date" v-model="record.date_of_discharge" />
                    </div>
                  </template>
                </div>
              </div>
            </div>
            <!-- ── DRUGS (primary care only) ── -->
            <div class="card shadow-sm mb-4" v-if="record.treatment_type === 'Primary'">
              <div class="card-header border-0 pt-4 pb-2 px-4" style="background: #f8f9fa;">
                <div class="d-flex align-items-center">
                  <span class="badge badge-info mr-2"
                    style="border-radius: 50%; width: 26px; height: 26px; line-height: 26px; font-size: 13px;">{{ sectionNo.drugs }}</span>
                  <h6 class="mb-0 font-weight-bold text-dark">Drugs</h6>
                </div>
              </div>
              <div class="card-body px-4 pt-3">
                <p v-if="record.treatment_type === 'Primary' && catalogProgramme" class="small text-muted mb-3">
                  <i class="fe fe-filter mr-1"></i> Showing what applies to <b>{{ catalogProgramme }}</b>
                  ({{ drugOptions.length }} drugs).
                </p>
                <div v-for="entry in serviceRows('Drug')" :key="'Drug-' + entry.index"
                  class="p-3 mb-3 rounded" style="background: #f8fafc; border: 1px solid #e9ecef;">
                  <div class="form-row align-items-end">
                    <div class="form-group col-md-6 mb-2">
                      <label class="small font-weight-bold text-secondary">Select Drug</label>
                      <div :class="serviceErrors.includes(entry.index) ? 'is-invalid-select' : ''">
                        <v-select v-model="entry.svc.drug" label="item_data" :options="drugOptions"
                          @input="removeServiceError(entry.index)"
                          placeholder="Search drug…" />
                      </div>
                      <small v-if="serviceErrors.includes(entry.index)" class="text-danger">Please select a drug</small>
                    </div>
                    <div class="form-group col-md-5 mb-2">
                      <label class="small font-weight-bold text-secondary d-block">Input Mode</label>
                      <div class="btn-group btn-group-sm" role="group">
                        <button type="button" class="btn"
                          :class="entry.svc.doseMode === 'simple' ? 'btn-info' : 'btn-outline-secondary'"
                          @click="entry.svc.doseMode = 'simple'; entry.svc.dose = 1; entry.svc.frequency = 1">
                          <i class="fe fe-box mr-1"></i> Quantity
                        </button>
                        <button type="button" class="btn"
                          :class="entry.svc.doseMode === 'advanced' ? 'btn-info' : 'btn-outline-secondary'"
                          @click="entry.svc.doseMode = 'advanced'">
                          <i class="fe fe-sliders mr-1"></i> Dose / Freq / Days
                        </button>
                      </div>
                    </div>
                    <div class="form-group col-md-1 mb-2 text-right">
                      <label class="small font-weight-bold text-secondary d-block">&nbsp;</label>
                      <button type="button" class="btn btn-sm btn-outline-danger" @click="removeArray(entry.index)" title="Remove">
                        <i class="fe fe-trash-2"></i>
                      </button>
                    </div>
                  </div>
                  <div class="form-row" v-if="entry.svc.doseMode === 'simple'">
                    <div class="form-group col-md-3 mb-0">
                      <label class="small font-weight-bold text-secondary">Quantity</label>
                      <input type="number" min="1" class="form-control form-control-sm"
                        v-model.number="entry.svc.days" placeholder="1" />
                    </div>
                  </div>
                  <div class="form-row" v-if="entry.svc.doseMode === 'advanced'">
                    <div class="form-group col-md-3 mb-0">
                      <label class="small font-weight-bold text-secondary">Dose</label>
                      <input type="number" min="1" class="form-control form-control-sm"
                        v-model.number="entry.svc.dose" placeholder="1" />
                    </div>
                    <div class="form-group col-md-3 mb-0">
                      <label class="small font-weight-bold text-secondary">Frequency</label>
                      <select class="form-control form-control-sm" v-model.number="entry.svc.frequency">
                        <option value="1">Daily (OD)</option>
                        <option value="2">Twice daily (BD)</option>
                        <option value="3">Three times (TDS)</option>
                        <option value="4">Four times (QDS)</option>
                      </select>
                      <small class="text-muted">Times per day</small>
                    </div>
                    <div class="form-group col-md-3 mb-0">
                      <label class="small font-weight-bold text-secondary">Duration (Days)</label>
                      <input type="number" min="1" max="365" class="form-control form-control-sm"
                        v-model.number="entry.svc.days" placeholder="1" />
                    </div>
                  </div>
                </div>
                <button type="button" class="btn btn-sm btn-outline-info" @click="addArray('Drug')">
                  <i class="fe fe-plus mr-1"></i> Add Drug
                </button>
              </div>
            </div>

            <!-- ── REFERRAL (primary care only: a secondary treatment is already the referred care) ── -->
            <div class="card shadow-sm mb-4" v-if="record.treatment_type === 'Primary'">
              <div class="card-header border-0 pt-4 pb-2 px-4" style="background: #f8f9fa;">
                <div class="d-flex align-items-center">
                  <span class="badge badge-info mr-2"
                    style="border-radius: 50%; width: 26px; height: 26px; line-height: 26px; font-size: 13px;">{{ sectionNo.referral }}</span>
                  <h6 class="mb-0 font-weight-bold text-dark">Referral</h6>
                </div>
              </div>
              <div class="card-body px-4 pt-3">
                <div class="form-row align-items-end">
                  <div class="form-group col-md-3">
                    <label class="small font-weight-bold text-secondary">Refer for Secondary Care?</label>
                    <select required class="form-control" v-model="record.referral_question">
                      <option value="">Select…</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                  <div class="form-group col-md-4" v-if="record.referral_question == 'Yes'">
                    <label class="small font-weight-bold text-secondary">Select Referral Facility</label>
                    <v-select v-model="record.referred_to_facility" :reduce="agency_name => agency_name.id"
                      label="agency_name" :options="providers" :loading="providersLoading"
                      placeholder="Search facility…" />
                  </div>


                   <div class="form-group col-md-4" v-if="record.referral_question == 'Yes'">
                    <label class="small font-weight-bold text-secondary">Select Service(s) </label>
                     <v-select v-model="record.auth_services" label="description"
                      :reduce="description => description.id"
                      :options="serviceOptions" multiple />
                  </div>

                   <div class="form-group col-md-10" v-if="record.referral_question == 'Yes'">
                    <label class="small font-weight-bold text-secondary">Select Secondary Diagnosis </label>
                    <v-select v-model="record.auth_diagnosis" label="name"
                      :options="diseases"
                      :filterable="false"
                      @search="onDiagnosisSearch"
                      placeholder="ICD 11 Diagnosis…">
                      <template #option="option">
                        <span class="text-muted mr-1">({{ option.diagnosis_code }})</span>{{ option.name }}
                      </template>
                      <template #selected-option="option">
                        ({{ option.diagnosis_code }}) - {{ option.name }}
                      </template>
                      <template #no-options="{ search }">
                        <span class="text-muted small">{{ search.length >= 4 ? 'No results found' : 'Search ICD 11 Diagnoses…' }}</span>
                      </template>
                    </v-select>
                  </div>


                </div>
              </div>
            </div>

            <!-- ── SUBMIT ───────────────────────────────────────── -->
            <div class="card shadow-sm border-0">
              <div class="card-body px-4 py-3 d-flex justify-content-end" style="gap: 12px;">
                <button type="button" class="btn btn-outline-secondary px-4" @click="$router.go(-1)">
                  Cancel
                </button>
                <button class="btn btn-info px-5" type="submit">
                  <i class="fe fe-send mr-2"></i>
                  {{ record.treatment_type == 'Secondary' ? 'Proceed to Claims' : 'Submit Record' }}
                </button>
              </div>
            </div>

          </form>
        </div>
      </section>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="false" :is-full-page="fullPage"></loading>
      </div>
    </main>
  </section>
</template>

<script>
  import * as faceapi from "face-api.js";
  import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
  import Navbar from "@/views/Navbar.vue";
  import { enrolleeProgramme, forProgramme } from "@/utils/programmeCatalog";
  import Loading from "vue-loading-overlay";
  import "vue-loading-overlay/dist/vue-loading.css";

  let agency = JSON.parse(localStorage.getItem("agency"));

  export default {
    components: { Navbar, Loading },

    data() {
      return {
        agency,
        user: null,
        // Telemedicine consultation (?appointment=ID): prefills the patient and links the encounter to it.
        consultation: null,
        searchkey: "",
        selected_enrollee: {},
        diseases: [],
        diagnosisSearchTimeout: null,
        diagnosisError: false,
        serviceErrors: [],
        search_result: [],
        userDetails: false,
        isLoading: false,
        fullPage: true,
        services: "",
        drugs: "",
        providers: [],
        providersLoading: false,
        showVitals: false,
        faceVerify: {
          active: false,
          loading: false,
          comparing: false,
          capturedImage: null,
          storedImg: null,
          result: null,
          confidence: null,
        },
        encounter_facility: "",
        record: {
          provider_id: "",
          patient_id: "",
          reasonVisit: "",
          date_of_visit: new Date().toISOString().split('T')[0],
          drVisited: "",
          testResult: "Not filled",
          documents: "no document uploaded",
          medications: "notes",
          patient_type: "",
          diagnosis: "",
          diagnoses: "",
          other_diagnosis: "",
          date_of_admission: "",
          date_of_discharge: "",
          treatment_type: "",
          encounter_outcome: "",
          is_corridor: false,
          next_visit: "",
          family_history: "",
          history_taking: "",
          examination: "",
          referral_question: "No",
          referred_to_facility: "",
          // Vitals
          temperature: "",
          pulse_rate: "",
          respiratory_rate: "",
          systolic: "",
          diastolic: "",
          weight: "",
          height: "",
          bmi: "",
          blood_glucose: "",
          auth_services: null,
          auth_diagnosis: "",
          services: [],
        },
      };
    },

    computed: {

      // Agency staff have no facility of their own, so they choose one (the API checks it is the agency's).

      agencyStaff() {

        return !!this.user && this.user.type === "employee";

      },
      // The enrollee's programme; the drug and service pickers only offer what applies to it (utils/programmeCatalog).
      catalogProgramme() {
        return enrolleeProgramme(this.selected_enrollee);
      },
      serviceOptions() {
        return forProgramme(this.services, this.catalogProgramme);
      },
      drugOptions() {
        return forProgramme(this.drugs, this.catalogProgramme);
      },
      // Visit → patient → vitals → notes → investigations → diagnosis → drugs → referral; drugs and referral are
      // primary care only.
      sectionNo() {
        const primary = this.record.treatment_type === "Primary";
        return { investigations: 5, diagnosis: 6, drugs: primary ? 7 : null, referral: primary ? 8 : null };
      },
      bmiClass() {
        const bmi = parseFloat(this.record.bmi);
        if (!bmi) return "text-muted";
        if (bmi < 18.5) return "text-info";
        if (bmi < 25) return "text-success";
        if (bmi < 30) return "text-warning";
        return "text-danger";
      },
      bmiLabel() {
        const bmi = parseFloat(this.record.bmi);
        if (!bmi) return "—";
        if (bmi < 18.5) return "Underweight";
        if (bmi < 25) return "Normal";
        if (bmi < 30) return "Overweight";
        return "Obese";
      },
    },

    watch: {
      // A secondary treatment has no referral block, so it never submits a referral.
      'record.treatment_type'(val) {
        if (val === 'Secondary') {
          this.record.referral_question = 'No';
        }
      },
      'record.referral_question'(val) {
        if (val === 'Yes' && this.providers.length === 0) {
          this.getProviders();
        }
      },
    },

    beforeMount() {
      this.user = JSON.parse(localStorage.getItem("user"));
      if (this.agencyStaff) this.getProviders();

      const q = this.$route.query;
      this.record.treatment_type = (q.type === "secondary" || this.$route.params.type === "secondary")
        ? "Secondary"
        : "Primary";
      if (q.reason) this.record.reasonVisit = q.reason;
      if (q.enrollee) this.searchkey = q.enrollee;
    },

    created() {
      this.getDrugs();
      this.getServices();
    },

    mounted() {
      if (this.$route.query.appointment) {
        this.loadConsultation(this.$route.query.appointment);
      } else if (this.$route.query.enrollee) {
        this.searchIDCard();
      }
    },

    methods: {
      calculateBMI() {
        const w = parseFloat(this.record.weight);
        const h = parseFloat(this.record.height);
        if (w > 0 && h > 0) {
          const hm = h / 100;
          this.record.bmi = (w / (hm * hm)).toFixed(1);
        } else {
          this.record.bmi = "";
        }
      },

      onDiagnosisSearch(query, loading) {
        clearTimeout(this.diagnosisSearchTimeout);
        if (query.trim().length < 4) {
          if (query.trim().length === 0) this.diseases = [];
          return;
        }
        loading(true);
        this.diagnosisSearchTimeout = setTimeout(() => {
          const q = query.trim().toLowerCase();
          this.axios
            .post(`/api/v1/auth/diagnosis-search`, { name: q })
            .then((response) => {
              const all = Array.isArray(response.data) ? response.data : (response.data.data || []);
              this.diseases = all.filter(d => d.name && d.name.toLowerCase().includes(q));
              loading(false);
            })
            .catch((error) => {
              console.error(error);
              loading(false);
            });
        }, 400);
      },

      searchIDCard() {
        this.isLoading = true;
        this.axios
          .post(`/api/v1/auth/searchenrollees`, {
            agency_id: this.record.is_corridor === false ? agency.id : null,
            request_query: this.searchkey,
          })
          .then((response) => {
            this.search_result = response.data;
            this.userDetails = true;
            this.isLoading = false;
            this.selectConsultationPatient();
            this.$toasted.info("Search complete", { position: "top-center", duration: 2000 });
          })
          .catch((error) => {
            console.error(error);
            this.isLoading = false;
          });
      },

      loadConsultation(id) {
        this.axios
          .get(`/api/v1/auth/telemedicine/consultations/${id}`)
          .then((response) => {
            const c = response.data.data;
            if (!response.data.is_consulting_doctor) {
              this.$toasted.error("Only the telemedicine facility of this consultation can record its encounter.", { position: "top-center", duration: 5000 });
              return;
            }
            if (c.service_summary_id) {
              this.$toasted.info("An encounter is already recorded for this consultation.", { position: "top-center", duration: 4000 });
              this.$router.replace(`/encounter/${c.service_summary_id}`);
              return;
            }
            this.consultation = c;
            this.record.reasonVisit = "Telemedicine Consultation";
            this.record.treatment_type = "Primary";
            this.searchkey = c.patient_id_card || "";
            if (this.searchkey) this.searchIDCard();
          })
          .catch(() => {
            this.$toasted.error("Could not load the telemedicine consultation.", { position: "top-center", duration: 4000 });
          });
      },

      // After a search, picks the consultation's patient (principal or dependant) from the results, or the
      // enrollee opened from the QR verify page (?patient=client:<users.id> or dependent:<dependants.id>).
      selectConsultationPatient() {
        if (!Array.isArray(this.search_result)) return;
        const wanted = /^(client|dependent):(\d+)$/.exec(this.$route.query.patient || "");
        if (!this.consultation && wanted) {
          const type = wanted[1] === "client" ? "client" : "Dependent";
          const found = this.search_result.find((r) => r.type === type && Number(r.id) === Number(wanted[2]));
          if (found) this.selected_enrollee = found;
          return;
        }
        if (!this.consultation) return;
        const c = this.consultation;
        const match = this.search_result.find((r) => (c.dependent_id
          ? r.type === "Dependent" && Number(r.id) === Number(c.dependent_id)
          : r.type === "client" && Number(r.id) === Number(c.patient_id)));
        if (match) this.selected_enrollee = match;
      },

      AddRecord() {

        if (this.agencyStaff && !this.encounter_facility) {

          this.$toasted.error("Choose the facility where the patient was seen.", { position: "top-center", duration: 5000 });

          return;

        }
        this.user = JSON.parse(localStorage.getItem("user"));

        if (!this.record.diagnosis) {
          this.diagnosisError = true;
          this.$toasted.error("Main Diagnosis is required. Please select one from the list.", { position: "top-center", duration: 4000 });
          return;
        }

        if (this.record.treatment_type === 'Primary') {
          this.serviceErrors = [];
          this.record.services.forEach((svc, i) => {
            if (svc.type === 'Drug' && !svc.drug) this.serviceErrors.push(i);
            if (svc.type === 'Service' && !svc.service) this.serviceErrors.push(i);
          });
          if (this.serviceErrors.length) {
            this.$toasted.error("Please select a drug or service for all highlighted rows.", { position: "top-center", duration: 4000 });
            return;
          }
        }

        // The enrollee's number on record is sent with the encounter; a wrong one is refused, so say so first.
        const phone = String((this.selected_enrollee && this.selected_enrollee.phone_number) || "").trim();
        if (phone && !/^\d{11}$/.test(phone)) {
          this.$toasted.error(`This enrollee's phone number on record ("${phone}") is not valid. Correct it in the phone field under the enrollee's name (11 digits, e.g. 08031234567) or clear it, then submit again.`, { position: "top-center", duration: 8000 });
          if (this.$refs.enrolleePhone) this.$refs.enrolleePhone.focus();
          return;
        }

        if (confirm("Are you sure you want to submit this record?")) {
          this.isLoading = true;
          this.axios
            .post("/api/v1/auth/addHealthRecord", {
              // A facility account records for itself; facility staff for their facility.
              provider_id: this.agencyStaff ? this.encounter_facility : (this.user.type === "provider" ? this.user.id : this.user.institutional_id),
              appointment_id: this.consultation ? this.consultation.id : null,
              patient_id: this.selected_enrollee.type === "client" ? this.selected_enrollee.id : 0,
              dependent_id: this.selected_enrollee.type === "Dependent" ? this.selected_enrollee.id : null,
              reasonVisit: this.record.reasonVisit,
              testResult: this.record.testResult,
              agency_id: agency.id,
              drVisited: this.user.id,
              professional_id: this.user.id,
              notes: this.record.medications,
              diagnosis: this.record.diagnosis ? this.record.diagnosis.id : null,
              diagnoses: Array.isArray(this.record.diagnoses) ? this.record.diagnoses.map(d => d.id) : [],
              other_diagnosis: this.record.other_diagnosis,
              phone_number: this.selected_enrollee.phone_number,
              patient_type: this.record.patient_type,
              medications: "view service rendered page for more details",
              documents: this.record.documents,
              treatment_type: this.record.treatment_type,
              encounter_outcome: this.record.encounter_outcome,
              date_of_admission: this.record.date_of_admission,
              date_of_discharge: this.record.date_of_discharge,
              date_of_visit: this.record.date_of_visit,
              next_visit: this.record.next_visit,
              family_history: this.record.family_history,
              history_taking: this.record.history_taking,
              examination: this.record.examination,
              // A secondary treatment is already the referred care: no referral, drugs or services from this page.
              referral_question: this.record.treatment_type === "Primary" ? this.record.referral_question : "No",
              referred_to_facility: this.record.treatment_type === "Primary" ? this.record.referred_to_facility : null,
              services: this.record.treatment_type === "Primary" ? this.record.services : [],
              auth_diagnosis: this.record.treatment_type === "Primary" && this.record.auth_diagnosis ? this.record.auth_diagnosis.id : null,
              auth_services: this.record.treatment_type === "Primary" ? this.record.auth_services : [],
              enrollee_image: this.faceVerify.capturedImage || null,
              // Vitals
              temperature: this.record.temperature,
              pulse_rate: this.record.pulse_rate,
              respiratory_rate: this.record.respiratory_rate,
              systolic: this.record.systolic,
              diastolic: this.record.diastolic,
              weight: this.record.weight,
              height: this.record.height,
              bmi: this.record.bmi,
              blood_glucose: this.record.blood_glucose,
            })
            .then((response) => {
              this.isLoading = false;
              this.$toasted.success("Record added successfully!", { position: "top-center", duration: 3000 });
              const record_id = response.data.service_summary.id;
              if (this.consultation) {
                // Back to the consultation: referral codes, claim (when the agency pays) and the record.
                this.$router.push(`/appointment/${this.consultation.id}`);
                return;
              }
              // Agency staff stop at the encounter: claims are for the facility to raise.
              this.$router.push(this.agencyStaff || this.record.treatment_type === "Primary" ? `/encounter/${record_id}` : `/add-claim/${record_id}`);
            })
            .catch((error) => {
              console.log(error.response);
              this.isLoading = false;
              const message = error.response && error.response.data && error.response.data.message;
              this.$toasted.error(message || "Submission failed. Please check all required fields.", { position: "top-center", duration: 8000 });
            });
        }
      },

      getDrugs() {
        this.axios.get(`/api/v1/auth/drug-agency/${agency.id}`)
          .then((response) => { this.drugs = response.data; })
          .catch((error) => { console.error(error); });
      },

      getServices() {
        this.axios.get(`/api/v1/auth/service-agency/${agency.id}`)
          .then((response) => { this.services = response.data; })
          .catch((error) => { console.error(error); });
      },

      getProviders() {
        this.providersLoading = true;
        this.axios.get(`/api/v1/auth/providerAgency/${agency.id}`)
          .then((response) => { this.providers = response.data.data; })
          .catch((error) => {
            console.error(error);
            this.$toasted.error("Failed to load facilities", { position: "top-center", duration: 3000 });
          })
          .finally(() => { this.providersLoading = false; });
      },

      // Rows of record.services of one kind (Service = investigations/services, Drug), with their index in the full list.
      serviceRows(kind) {
        return this.record.services.map((svc, index) => ({ svc, index })).filter((e) => e.svc.type === kind);
      },
      addArray(type = "Drug") {
        this.record.services.push({ drug: null, service: null, type, dose: 1, frequency: 1, days: 1, doseMode: "simple" });
      },

      removeArray(index) {
        this.record.services.splice(index, 1);
        this.removeServiceError(index);
      },
      removeServiceError(index) {
        const i = this.serviceErrors.indexOf(index);
        if (i !== -1) this.serviceErrors.splice(i, 1);
      },

      async startFaceVerify() {
        this.faceVerify.active = true;
        this.faceVerify.loading = true;
        this.faceVerify.result = null;
        this.faceVerify.capturedImage = null;
        this.faceVerify.storedImg = null;

        if (!faceapi.nets.tinyFaceDetector.isLoaded) {
          try {
            const MODEL_URL = "/models";
            await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
            await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
            await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
          } catch (e) {
            console.error("Model load failed", e);
            this.$toasted.error("Failed to load face recognition models.", { position: "top-center", duration: 4000 });
            this.faceVerify.loading = false;
            return;
          }
        }

        this.faceVerify.loading = false;
      },

      async captureForVerify() {
        try {
          const photo = await Camera.getPhoto({
            quality: 90,
            resultType: CameraResultType.Base64,
            source: CameraSource.Camera,
            format: "jpeg",
          });
          this.faceVerify.capturedImage = `data:image/jpeg;base64,${photo.base64String}`;
          this.faceVerify.result = null;
          this.compareFaces();
        } catch (e) {
          console.error("Camera error", e);
        }
      },

      async compareFaces() {
        this.faceVerify.comparing = true;
        this.faceVerify.result = null;
        try {
          const opts = new faceapi.TinyFaceDetectorOptions({ inputSize: 608, scoreThreshold: 0.5 });

          // Load stored enrollee image now (at compare time so we get a real error if it fails)
          if (!this.faceVerify.storedImg) {
            try {
              const imageUrl = `https://claimify-api.hayokmedicare.ng/api/v1/auth/image/${this.selected_enrollee.user_image}`;
              this.faceVerify.storedImg = await faceapi.fetchImage(imageUrl);
            } catch (imgErr) {
              console.error("Stored image load error:", imgErr);
              this.$toasted.error(
                `Could not load enrollee photo: ${imgErr.message || imgErr}. Check that /api/image/ route is set up on the backend.`,
                { position: "top-center", duration: 6000 }
              );
              this.faceVerify.comparing = false;
              return;
            }
          }

          // Detect from stored enrollee image
          const storedDetection = await faceapi
            .detectSingleFace(this.faceVerify.storedImg, opts)
            .withFaceLandmarks()
            .withFaceDescriptor();

          // Detect from captured image
          const capturedEl = document.createElement("img");
          capturedEl.src = this.faceVerify.capturedImage;
          await new Promise((res) => { capturedEl.onload = res; });
          const capturedDetection = await faceapi
            .detectSingleFace(capturedEl, opts)
            .withFaceLandmarks()
            .withFaceDescriptor();

          if (!storedDetection || !capturedDetection) {
            this.faceVerify.result = "no-face";
          } else {
            const distance = faceapi.euclideanDistance(
              storedDetection.descriptor,
              capturedDetection.descriptor
            );
            const confidence = Math.round(Math.max(0, (1 - distance) * 100));
            this.faceVerify.confidence = confidence;
            this.faceVerify.result = distance < 0.5 ? "match" : "no-match";
          }
        } catch (e) {
          console.error("Face comparison error", e);
          this.$toasted.error("Comparison failed. Try again.", { position: "top-center", duration: 3000 });
        } finally {
          this.faceVerify.comparing = false;
        }
      },

      resetFaceVerify() {
        this.faceVerify.active = false;
        this.faceVerify.capturedImage = null;
        this.faceVerify.storedImg = null;
        this.faceVerify.result = null;
        this.faceVerify.confidence = null;
      },

      sendSMS() {
        // Disabled: this called multitexter.com's SMS API with a hardcoded
        // credential embedded in frontend source (a leaked secret visible to
        // every browser). Re-enable only via a backend endpoint that holds
        // the credential server-side.
        console.warn("sendSMS() is disabled pending a backend SMS endpoint.");
      },
    },
  };
</script>

<style scoped>
  .is-invalid-select .vs__dropdown-toggle {
    border-color: #dc3545 !important;
    box-shadow: 0 0 0 0.15rem rgba(220, 53, 69, .15);
  }

  .card {
    border-radius: 12px;
    border: 1px solid #e9ecef;
  }

  .card-header {
    border-radius: 12px 12px 0 0 !important;
  }

  .input-group-text {
    font-size: 12px;
    color: #6c757d;
  }

  .form-control:focus {
    border-color: #17a2b8;
    box-shadow: 0 0 0 0.15rem rgba(23, 162, 184, 0.2);
  }
</style>